import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";

import {
    SchedulingType,
    ControlsAllColumn,
    ControlsPageFiltersForControlsAllPageSpecificFilters as ControlsAllFilters,
    ControlsAllPageSpecificFilters,
} from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { PageFilterAndColumns } from "org/pageState/types";
import { unwrapPageStateAs } from "org/pageState/utils";

import { decodeOrFilter, encodeOrFilter } from "../common";

import {
    createDecodePageFiltersFn,
    createEncodeColumnFn,
    createEncodePageFiltersFn,
} from "./common";

type AllColumns = { [K in ControlsAllColumn]: null };
const allColumnsGuard: AllColumns = {
    name: null,
    description: null,
    program: null,
    status: null,
    schedulingType: null,
    assignment: null,
    loginProtection: null,
    segregationOfDuties: null,
    tags: null,
    accessRight: null,
    controlResultCount: null,
    nextDueDate: null,
};
const ALL_COLUMNS: ControlsAllColumn[] = Object.keys(
    allColumnsGuard
) as ControlsAllColumn[];

const encodeControlsAllColumn: (col: string) => ControlsAllColumn =
    createEncodeColumnFn(ALL_COLUMNS);

// Filters
//
function encodeControlsAllSpecificPageFilters(
    filters: Array<SimpleOrFilter>
): ControlsAllPageSpecificFilters {
    return {
        schedulingType: encodeOrFilter<SchedulingType>(
            "schedulingType",
            filters
        ),
    };
}
const encodeControlsAllPageFilters: (
    simpleAndFilter: SimpleAndFilter | null
) => ControlsAllFilters = createEncodePageFiltersFn(
    encodeControlsAllSpecificPageFilters
);

function decodeControlsAllPageSpecificFilters(
    specificFilters: ControlsAllPageSpecificFilters
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];
    if (specificFilters.schedulingType) {
        output.push(
            decodeOrFilter<SchedulingType>(
                "schedulingType",
                specificFilters.schedulingType
            )
        );
    }
    return output;
}
const decodeControlsAllPageFilters: (
    pageFilters: ControlsAllFilters
) => Array<SimpleOrFilter> = createDecodePageFiltersFn(
    decodeControlsAllPageSpecificFilters
);

// Page state
//
type ControlsAllPageStateWithType = PageStateData<"ControlsAll"> & {
    type: "ControlsAll";
};

function encodeControlsAllPageState(
    filterAndColumns: PageFilterAndColumns
): ControlsAllPageStateWithType {
    const { filter, visibleColumnIds } = filterAndColumns;

    return {
        type: "ControlsAll",
        filters: encodeControlsAllPageFilters(filter),
        columns: Array.from(visibleColumnIds).map(encodeControlsAllColumn),
        version: filterAndColumns.version,
    };
}

function decodeControlsAllPageState(
    pageState: ControlsAllPageStateWithType
): PageFilterAndColumns {
    const allPageState = unwrapPageStateAs(pageState, "ControlsAll");
    const filter: SimpleAndFilter = {
        filters: decodeControlsAllPageFilters(allPageState.filters),
    };
    const visibleColumnIds = new Set(allPageState.columns);
    return {
        pageType: pageState.type,
        filter,
        visibleColumnIds,
        version: pageState.version ?? 0,
    };
}

export { encodeControlsAllPageState, decodeControlsAllPageState };

export type { ControlsAllPageStateWithType };
